import React, { Component } from 'react';
import {
    Alert,
    Autocomplete,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    MenuItem,
    Stack,
    TextField
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';

import i18n from '../../i18n';
import { AppDataContext } from '../../context/Context';
import { formatBackendError } from '../../utils/formatError';

const GENDER_OPTIONS = ['male', 'female', 'other'];

const REQUIRED_FIELDS = [
    'firstName',
    'lastName',
    'dateOfBirth',
    'email',
    'address',
    'passport'
];

const buildInitialData = (user) => ({
    firstName: user?.firstName ?? '',
    lastName: user?.lastName ?? '',
    middleName: user?.middleName ?? '',
    dateOfBirth: user?.dateOfBirth ?? '',
    gender: user?.gender ?? '',
    email: user?.email ?? '',
    countryCode: user?.country?.countryCode ?? '',
    city: user?.city ?? '',
    postCode: user?.postCode ?? '',
    address: user?.address ?? '',
    passport: ''
});

class KycApproveDialog extends Component {
    static contextType = AppDataContext;

    constructor(props) {
        super(props);
        this.state = {
            data: buildInitialData(props.user),
            errors: [],
            loading: false
        };
    }

    componentDidUpdate(prevProps) {
        if (!prevProps.open && this.props.open) {
            this.setState({
                data: buildInitialData(this.props.user),
                errors: [],
                loading: false
            });
        }
    }

    setField = (field, value) => {
        this.setState((prev) => ({
            data: { ...prev.data, [field]: value }
        }));
    };

    handleTextChange = (field) => (event) => {
        this.setField(field, event.target.value);
    };

    handleDateChange = (value) => {
        if (!value) {
            this.setField('dateOfBirth', '');
            return;
        }
        const formatted = moment(value).format('YYYY-MM-DD');
        this.setField('dateOfBirth', formatted);
    };

    handleCountryChange = (event, option) => {
        this.setField('countryCode', option?.countryCode ?? '');
    };

    showSnackbar = (message, severity) => {
        this.context.update({
            alertError: { message, type: 'snackbar', severity }
        });
    };

    handleSave = async () => {
        const { data } = this.state;
        const missing = REQUIRED_FIELDS.filter(
            (f) => !String(data[f] ?? '').trim()
        );
        if (missing.length) {
            this.setState({
                errors: missing.map(
                    (f) =>
                        `${i18n.t(`columns.${f}`)}: ${i18n.t(
                            'errors.fieldRequired'
                        )}`
                )
            });
            return;
        }

        this.setState({ loading: true, errors: [] });
        try {
            const result = await this.context
                .getState()
                .server.walletUsersKyc.approve(this.props.user.id, data);

            if (result.error === 'OK') {
                this.showSnackbar(
                    i18n.t('errors.kycApproveSuccess'),
                    'success'
                );
                this.props.onClose?.(true);
            } else {
                const formatted = formatBackendError(result);
                this.setState({
                    errors: Array.isArray(formatted)
                        ? formatted
                        : [formatted].filter(Boolean)
                });
                this.showSnackbar(
                    i18n.t('errors.kycApproveFailed'),
                    'error'
                );
            }
        } catch (err) {
            this.setState({ errors: [err.message] });
            this.showSnackbar(i18n.t('errors.kycApproveFailed'), 'error');
        } finally {
            this.setState({ loading: false });
        }
    };

    getCountries = () => {
        return this.context.getState().controllersData?.publicCountries || [];
    };

    renderCountryField() {
        const { data } = this.state;
        const countries = this.getCountries();
        const selected =
            countries.find((c) => c.countryCode === data.countryCode) || null;

        return (
            <Autocomplete
                value={selected}
                options={countries}
                getOptionLabel={(option) =>
                    option?.name
                        ? `${option.name} (${option.countryCode})`
                        : ''
                }
                isOptionEqualToValue={(option, value) =>
                    option.countryCode === value.countryCode
                }
                onChange={this.handleCountryChange}
                renderOption={(props, option) => (
                    <li {...props} key={option.id ?? option.countryCode}>
                        {option.name} ({option.countryCode})
                    </li>
                )}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label={i18n.t('columns.country')}
                        size="small"
                    />
                )}
            />
        );
    }

    renderDateField() {
        const { data } = this.state;
        const value = data.dateOfBirth
            ? moment(data.dateOfBirth, 'YYYY-MM-DD', true)
            : null;
        const locale = i18n.language === 'en' ? 'en-gb' : i18n.language;

        return (
            <LocalizationProvider
                dateAdapter={AdapterMoment}
                adapterLocale={locale}
            >
                <DatePicker
                    sx={{ width: '100%' }}
                    label={i18n.t('columns.dateOfBirth')}
                    value={value && value.isValid() ? value : null}
                    onChange={this.handleDateChange}
                    slotProps={{ textField: { size: 'small', required: true } }}
                />
            </LocalizationProvider>
        );
    }

    renderGenderField() {
        const { data } = this.state;
        return (
            <TextField
                select
                fullWidth
                size="small"
                label={i18n.t('columns.gender')}
                value={data.gender || ''}
                onChange={this.handleTextChange('gender')}
            >
                <MenuItem value="">
                    <em>{i18n.t('common.notSet')}</em>
                </MenuItem>
                {GENDER_OPTIONS.map((g) => (
                    <MenuItem key={g} value={g}>
                        {i18n.t(`enums.${g}`)}
                    </MenuItem>
                ))}
            </TextField>
        );
    }

    renderTextField(id, options = {}) {
        const { data } = this.state;
        return (
            <TextField
                fullWidth
                size="small"
                label={i18n.t(`columns.${id}`)}
                value={data[id] ?? ''}
                onChange={this.handleTextChange(id)}
                {...options}
            />
        );
    }

    render() {
        const { open, onClose } = this.props;
        const { errors, loading } = this.state;

        return (
            <Dialog
                open={open}
                onClose={() => onClose?.(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>{i18n.t('text.kycApproveTitle')}</DialogTitle>
                <DialogContent>
                    {errors.length > 0 && (
                        <Alert severity="error" sx={{ mb: 1, mt: 1 }}>
                            {errors.map((message, idx) => (
                                <div key={idx}>{message}</div>
                            ))}
                        </Alert>
                    )}
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        {this.renderTextField('firstName', { required: true })}
                        {this.renderTextField('lastName', { required: true })}
                        {this.renderTextField('middleName')}
                        {this.renderDateField()}
                        {this.renderGenderField()}
                        {this.renderTextField('email', {
                            required: true,
                            type: 'email'
                        })}
                        {this.renderCountryField()}
                        {this.renderTextField('city')}
                        {this.renderTextField('postCode')}
                        {this.renderTextField('address', { required: true })}
                        {this.renderTextField('passport', { required: true })}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button
                        disabled={loading}
                        onClick={() => onClose?.(false)}
                    >
                        {i18n.t('buttons.cancel')}
                    </Button>
                    <LoadingButton
                        variant="contained"
                        loading={loading}
                        onClick={this.handleSave}
                    >
                        {i18n.t('buttons.approve')}
                    </LoadingButton>
                </DialogActions>
            </Dialog>
        );
    }
}

export default KycApproveDialog;
